import React from "react";
import { Typography } from "@mui/material";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TuneIcon from "@mui/icons-material/Tune";
import BugReportIcon from "@mui/icons-material/BugReport";

const ACCENT = "#c79656";
const BORDER = "#EAECEF";
const TEXT = "#1a1a1a";
const MUTED = "rgba(26,26,26,0.55)";

type ChangeType = "new" | "improved" | "fixed";

interface Change {
    type: ChangeType;
    text: string;
    date?: string;
}

interface Release {
    version: string;
    title: string;
    period: string;
    changes: Change[];
}

/**
 * Release history. Versions are curated from the project's commit history and
 * grouped into themed milestones — newest first.
 */
const RELEASES: Release[] = [
    {
        version: "0.9.4",
        title: "Sign-In & Payout Fixes",
        period: "August 2026",
        changes: [
            { type: "fixed", text: "Changing the payout denominations or rounding mode on the manual count page now updates the calculations straight away, instead of only after a refresh.", date: "Aug 15" },
            { type: "fixed", text: "An account that is temporarily locked after too many failed attempts now says so, instead of reporting the correct password as wrong.", date: "Aug 15" },
            { type: "fixed", text: "Signing in is no longer reported as failed when the follow-up security check hiccups — you stay signed in instead of having to reload.", date: "Aug 15" },
            { type: "fixed", text: "Sign-in problems now show the real reason rather than always blaming your username or password.", date: "Aug 15" },
            { type: "fixed", text: "Your account is recognised immediately on sign-in, so count history shows your name without needing a page refresh.", date: "Aug 15" },
        ],
    },
    {
        version: "0.9.3",
        title: "Debt Preview",
        period: "July 2026",
        changes: [
            { type: "improved", text: "The manual count list now shows, per worker, the debt the counted checkbox will move to — so you can see the change before ticking it.", date: "Jul 5" },
            { type: "improved", text: "Replaced the after-the-fact debt snackbar with this inline preview.", date: "Jul 5" },
        ],
    },
    {
        version: "0.9.2",
        title: "Debt That Adds Up",
        period: "July 2026",
        changes: [
            { type: "new", text: "Counting a worker manually now settles their tips: it pays out a fitted amount and carries the leftover as debt, shown as an old → new change.", date: "Jul 5" },
            { type: "new", text: "Unchecking a counted worker reverses exactly the debt that counting added, tracked per count event.", date: "Jul 5" },
            { type: "new", text: "Choose your payout denominations and exact/rounded mode in Settings, or adjust them on the fly from the manual and auto count pages.", date: "Jul 5" },
            { type: "fixed", text: "Debt is now coloured consistently across the counting screens.", date: "Jul 5" },
            { type: "fixed", text: "Rounded payouts now handle amounts a worker owes back instead of dropping them.", date: "Jul 5" },
            { type: "fixed", text: "Auto count no longer leaves a stored debt out of sync when marking a worker counted fails.", date: "Jul 5" },
        ],
    },
    {
        version: "0.9.1",
        title: "Manual Count Polish",
        period: "July 2026",
        changes: [
            { type: "improved", text: "Redesigned the manual count status and received-amount cards into one consistent, cleaner layout.", date: "Jul 4" },
        ],
    },
    {
        version: "0.9.0",
        title: "Audit & Merge",
        period: "July 2026",
        changes: [
            { type: "new", text: "Manual count audit history — see exactly which worker counted each shift and when.", date: "Jul 4" },
            { type: "new", text: "Merge duplicate workers into a single profile without losing history.", date: "May 16" },
            { type: "improved", text: "Redesigned the Add Work Period dialog for faster entry.", date: "Jun 27" },
            { type: "improved", text: "Location settings consolidated into a dedicated repository for reliability.", date: "May 16" },
        ],
    },
    {
        version: "0.8.0",
        title: "Worker Insights",
        period: "April – May 2026",
        changes: [
            { type: "new", text: "Dedicated Worker Details page with per-worker earnings and shift breakdowns.", date: "May 3" },
            { type: "new", text: "Import shifts and workers directly from a CSV file.", date: "Apr 26" },
            { type: "fixed", text: "Resolved a CSRF issue that could block secure requests.", date: "Apr 26" },
        ],
    },
    {
        version: "0.7.0",
        title: "Work Periods",
        period: "April 2026",
        changes: [
            { type: "new", text: "Split a single work day into multiple work periods.", date: "Apr 23" },
            { type: "new", text: "Delete button for removing records inline.", date: "Apr 23" },
            { type: "improved", text: "Refreshed the Tip Jar with clearer figures and layout.", date: "Apr 9" },
            { type: "improved", text: "Tighter, more consistent spacing across the app.", date: "Apr 24" },
            { type: "fixed", text: "Fixed an update bug when saving changes to a work day.", date: "Apr 23" },
        ],
    },
    {
        version: "0.6.0",
        title: "Manual Counting & Team",
        period: "March 2026",
        changes: [
            { type: "new", text: "New Manual Count page to close out and reconcile a shift.", date: "Mar 15" },
            { type: "new", text: "User details dialog for quick profile edits.", date: "Mar 25" },
            { type: "improved", text: "Redesigned the home dashboard for a cleaner overview.", date: "Mar 26" },
            { type: "improved", text: "UI polish across the Team page.", date: "Mar 22" },
            { type: "fixed", text: "Several bugfixes on the manual count page.", date: "Mar 22" },
        ],
    },
    {
        version: "0.5.0",
        title: "Count Periods & Balances",
        period: "February 2026",
        changes: [
            { type: "new", text: "Count Period management page to organise counting cycles.", date: "Feb 1" },
            { type: "new", text: "Transaction and balance-state tracking behind the scenes.", date: "Feb 3" },
            { type: "improved", text: "Redesigned the Tip Jar graphs.", date: "Feb 10" },
            { type: "improved", text: "Refinements to the Auto Count page.", date: "Feb 21" },
        ],
    },
    {
        version: "0.4.0",
        title: "Auto Count & Help",
        period: "January 2026",
        changes: [
            { type: "new", text: "Guided Auto Count flow with dedicated start and end steps.", date: "Jan 1" },
            { type: "new", text: "Tip received button with month-by-month tracking.", date: "Jan 3" },
            { type: "new", text: "Added the Help & Information page.", date: "Jan 30" },
            { type: "improved", text: "Date selector and navigation bar for the auto count flow.", date: "Jan 1" },
            { type: "fixed", text: "Bugfixes on the auto count page.", date: "Jan 3" },
        ],
    },
    {
        version: "0.3.0",
        title: "Counted State",
        period: "November – December 2025",
        changes: [
            { type: "new", text: "Counted state on work days and the calendar so you know what's done.", date: "Dec 22" },
            { type: "new", text: "Move days around by drag and drop.", date: "Nov 22" },
            { type: "improved", text: "Separate connections for development and production.", date: "Dec 19" },
        ],
    },
    {
        version: "0.2.0",
        title: "Tip Jar, Routing & Login",
        period: "August – October 2025",
        changes: [
            { type: "new", text: "Tip Jar page with charts for cash and pin.", date: "Sep 13" },
            { type: "new", text: "Auto Count page for automated tallies.", date: "Sep 26" },
            { type: "new", text: "App-wide routing and a refreshed login with company logo.", date: "Oct 11" },
            { type: "improved", text: "Redesigned the overview dashboard.", date: "Sep 30" },
            { type: "fixed", text: "Username display and result-ordering fixes.", date: "Oct 14" },
        ],
    },
    {
        version: "0.1.0",
        title: "Foundations",
        period: "June – July 2025",
        changes: [
            { type: "new", text: "First TipCounter release.", date: "Jun 18" },
            { type: "new", text: "Login and authentication.", date: "Jul 13" },
            { type: "new", text: "Work day management: add, open, edit and delete.", date: "Jul 13" },
            { type: "new", text: "Counting page with tabbed layout.", date: "Jul 26" },
            { type: "new", text: "Overview page with the monthly tip graph.", date: "Jul 7" },
        ],
    },
];

const TYPE_META: Record<ChangeType, { label: string; fg: string; bg: string; icon: React.ReactNode }> = {
    new: { label: "New", fg: "#0f9d6a", bg: "#e8f7f0", icon: <AutoAwesomeIcon sx={{ fontSize: 14 }} /> },
    improved: { label: "Improved", fg: "#a9762e", bg: "#f6ecdb", icon: <TuneIcon sx={{ fontSize: 14 }} /> },
    fixed: { label: "Fixed", fg: "#5b6b82", bg: "#eef1f6", icon: <BugReportIcon sx={{ fontSize: 14 }} /> },
};

function TypeChip({ type }: { type: ChangeType }) {
    const meta = TYPE_META[type];
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                flexShrink: 0,
                height: 22,
                padding: "0 9px",
                borderRadius: 999,
                background: meta.bg,
                color: meta.fg,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.2,
                lineHeight: 1,
            }}
        >
            {meta.icon}
            {meta.label}
        </span>
    );
}

function ReleaseCard({ release, isLatest, isLast }: { release: Release; isLatest: boolean; isLast: boolean }) {
    return (
        <div style={{ display: "flex", gap: 18, position: "relative" }}>
            {/* Timeline rail */}
            <div style={{ position: "relative", width: 20, flexShrink: 0 }}>
                <div
                    style={{
                        position: "absolute",
                        left: 9,
                        top: 8,
                        bottom: isLast ? "auto" : -28,
                        height: isLast ? 40 : "auto",
                        width: 2,
                        background: `linear-gradient(180deg, ${ACCENT}55, ${BORDER})`,
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        left: 2,
                        top: 22,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: isLatest ? ACCENT : "#fff",
                        border: `3px solid ${ACCENT}`,
                        boxShadow: "0 0 0 4px #fbfbfc",
                    }}
                />
            </div>

            {/* Card */}
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    marginBottom: 20,
                    background: "#fff",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 16,
                    boxShadow: "0 4px 18px rgba(0,0,0,0.04)",
                    overflow: "hidden",
                }}
            >
                {/* Card header */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                        padding: "16px 20px",
                        borderBottom: `1px solid ${BORDER}`,
                        background: isLatest ? `${ACCENT}0d` : "#fcfcfd",
                    }}
                >
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            height: 26,
                            padding: "0 11px",
                            borderRadius: 8,
                            background: `${ACCENT}1a`,
                            border: `1px solid ${ACCENT}40`,
                            color: "#8a5f22",
                            fontSize: 13,
                            fontWeight: 800,
                            fontVariantNumeric: "tabular-nums",
                            letterSpacing: 0.2,
                        }}
                    >
                        v{release.version}
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: TEXT }}>{release.title}</span>

                    {isLatest && (
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                height: 22,
                                padding: "0 9px",
                                borderRadius: 999,
                                background: ACCENT,
                                color: "#fff",
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: 0.3,
                            }}
                        >
                            Latest
                        </span>
                    )}

                    <span style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 600, color: MUTED }}>
                        {release.period}
                    </span>
                </div>

                {/* Changes */}
                <div style={{ display: "flex", flexDirection: "column", padding: "6px 20px 12px" }}>
                    {release.changes.map((change, i) => (
                        <div
                            key={i}
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 12,
                                padding: "12px 0",
                                borderBottom: i < release.changes.length - 1 ? `1px solid ${BORDER}` : "none",
                            }}
                        >
                            <div style={{ paddingTop: 1 }}>
                                <TypeChip type={change.type} />
                            </div>
                            <Typography
                                variant="body2"
                                style={{ flex: 1, minWidth: 0, color: "#374151", lineHeight: 1.5 }}
                            >
                                {change.text}
                            </Typography>
                            {change.date && (
                                <span
                                    style={{
                                        flexShrink: 0,
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        color: MUTED,
                                        fontVariantNumeric: "tabular-nums",
                                        paddingTop: 2,
                                    }}
                                >
                                    {change.date}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function WhatsNewPage() {
    const latest = RELEASES[0];

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
                padding: 24,
                display: "flex",
                flexDirection: "column",
                gap: 18,
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
                <div
                    style={{
                        width: 46,
                        height: 46,
                        borderRadius: 14,
                        display: "grid",
                        placeItems: "center",
                        background: `${ACCENT}18`,
                        border: `1px solid ${ACCENT}33`,
                        color: ACCENT,
                    }}
                >
                    <NewReleasesIcon />
                </div>
                <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 26, fontWeight: 800, color: TEXT, letterSpacing: "-0.02em" }}>
                            What's New
                        </span>
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                height: 24,
                                padding: "0 10px",
                                borderRadius: 999,
                                background: `${ACCENT}1a`,
                                border: `1px solid ${ACCENT}40`,
                                color: "#8a5f22",
                                fontSize: 12.5,
                                fontWeight: 700,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            v{latest.version}
                        </span>
                    </div>
                    <span style={{ fontSize: 13.5, color: MUTED, fontWeight: 500 }}>
                        Product updates and release notes
                    </span>
                </div>
            </div>

            {/* Legend */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
                <TypeChip type="new" />
                <TypeChip type="improved" />
                <TypeChip type="fixed" />
            </div>

            {/* Scrollable timeline */}
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    paddingRight: 8,
                    paddingTop: 4,
                }}
            >
                <div style={{ maxWidth: 780 }}>
                    {RELEASES.map((release, i) => (
                        <ReleaseCard
                            key={release.version}
                            release={release}
                            isLatest={i === 0}
                            isLast={i === RELEASES.length - 1}
                        />
                    ))}

                    <div
                        style={{
                            textAlign: "center",
                            color: MUTED,
                            fontSize: 12.5,
                            fontWeight: 500,
                            padding: "4px 0 12px",
                            marginLeft: 38,
                        }}
                    >
                        That's the beginning — thanks for being here from the start.
                    </div>
                </div>
            </div>
        </div>
    );
}
